import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import { useGetSocialListeningTagsQuery, useUpdateSocialListeningTagsMutation } from '../../../Services/instagram/Instagram';
import { toast } from 'react-toastify';

// Define the props for the TagModal component
interface TagModalProps {
    open: boolean;
    onClose: () => void;
    tags: string[];
    tagInput: string;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onAddTag: () => void;
    onDeleteTag: (tag: string) => void;
    setTags: (tags: string[]) => void;
    refetchSocialListening: () => void;
    socialListening: string;
}

// Define the TagModal component
const TagModal: React.FC<TagModalProps> = ({
    open,
    onClose,
    tags,
    tagInput,
    onInputChange,
    onAddTag,
    onDeleteTag,
    setTags,
    refetchSocialListening,
    socialListening
}) => {

    const [updateSocialListeningTags] = useUpdateSocialListeningTagsMutation();
    const { data: socialListeningTagsData, status: socialListeningTagsStatus, refetch: refetchSocialListeningTags } = useGetSocialListeningTagsQuery({socialListening});

    useEffect(() => {
        if (socialListeningTagsData && Array.isArray(socialListeningTagsData.tags)) {
            setTags(socialListeningTagsData.tags);
        }
    }, [socialListeningTagsData, setTags]);

    // Only adds a chip, does not update API
    const handleAddTag = () => {
        onAddTag();
    }

    // Calls the API to update tags
    const handleAddTagUpdate = async () => {
        if (!tags || tags.length === 0) {
            toast.error('Please add at least one tag before submitting.');
            return;
        }
        try {
            const res = await updateSocialListeningTags({ body: { tags }, socialListening }).unwrap?.() ?? await updateSocialListeningTags({ body: { tags } });
            toast.success('Tags updated successfully!');
            refetchSocialListeningTags();
            refetchSocialListening();
            onClose();
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || 'Failed to update tags.');
        }
    }
    return (

        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                Add tags
                <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <TextField
                    label="New tag"
                    value={tagInput}
                    onChange={onInputChange}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (tagInput.trim()) {
                                onAddTag();
                            }
                        }
                    }}
                    onBlur={e => {
                        // Only add tag if input is not empty and not duplicate
                        if (tagInput.trim()) {
                            onAddTag();
                        }
                    }}
                    fullWidth
                    autoFocus
                    margin="dense"
                    sx={{
                        '& .MuiInputLabel-root': {
                            color: '#6366f1',
                        },
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: '#6366f1',
                            },
                        },
                    }}
                />
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', mt: 2, gap: 1.5 }}>
                    {tags.map(tag => (
                        <Chip
                            key={tag}
                            label={tag}
                            onDelete={() => onDeleteTag(tag)}
                            variant="outlined"
                            sx={{
                                fontWeight: 500,
                                fontSize: 13,
                                borderRadius: 2,
                                color: '#6366f1',
                                borderColor: '#6366f1',
                            }}
                        />
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onMouseDown={handleAddTag} variant="contained" sx={{ textTransform: 'none', backgroundColor: '#6366f1', '&:hover': { backgroundColor: '#4f46e5' } }}>Add tag</Button>
                <Button onClick={handleAddTagUpdate} variant="outlined" sx={{ textTransform: 'none', color: '#6366f1', borderColor: '#6366f1' }}>Update</Button>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#6366f1' }}>Close</Button>
            </DialogActions>

        </Dialog>
    )
}

export default TagModal;
